import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  FileUploadError,
  MAX_FILE_NAME_CHARS,
  NEW_MAX_FILE_SIZE_IN_MB,
} from 'src/app/models/upload';

@Injectable({
  providedIn: 'root',
})
export class BulkUploadValidationService {
  /**
   * Centralized validator for bulk upload files.
   * Returns a translated error message for the first failing rule (if any).
   * Rules include max count, size limit, supported types, and name length.
   */
  readonly MAX_FILE_SIZE_IN_MB = NEW_MAX_FILE_SIZE_IN_MB;

  constructor(private readonly translateService: TranslateService) {}

  /**
   * Validate a batch of files and return a localized error message.
   * Returns `undefined` when all files pass validation.
   */
  public validateFiles(
    files: File[],
    maxFiles: number,
    supportedTypes: string,
  ): string | undefined {
    const error = this.getFileErrorIfExists(files, supportedTypes, maxFiles);
    if (error) {
      return this.getErrorMessage(error);
    }
  }

  /**
   * Find the first validation error in the list of files.
   * Order matters: file count is checked before per-file validations.
   */
  private getFileErrorIfExists(
    files: File[],
    supportedDocumentTypes: string,
    maxFiles: number,
  ): undefined | { error: FileUploadError; file?: File; maxFiles?: number } {
    const possibleErrors = [
      {
        condition: files.length > maxFiles,
        error: { error: FileUploadError.TooManyFiles, maxFiles },
      },
      ...files.flatMap((file) => [
        {
          condition: this.checkFileExceedSizeLimit(file),
          error: { error: FileUploadError.SizeLimitExceed, file },
        },
        {
          condition:
            !this.isFileMimeTypeAccepted(supportedDocumentTypes, file.type) &&
            !this.isFileExtensionAccepted(supportedDocumentTypes, file.name),
          error: { error: FileUploadError.NotSupportedType, file },
        },
        {
          condition: file.name.length > MAX_FILE_NAME_CHARS,
          error: { error: FileUploadError.TooLongFileName, file },
        },
      ]),
    ];

    for (const possibleError of possibleErrors) {
      if (possibleError.condition) {
        return possibleError.error;
      }
    }
  }

  /**
   * Check if a file name ends with any allowed extension.
   */
  private isFileExtensionAccepted(
    supportedDocumentTypes: string,
    fileName: string,
    delimiter = ', ',
  ) {
    return supportedDocumentTypes
      .split(delimiter)
      .some((accepted) => fileName.toLowerCase().endsWith(accepted.toLowerCase()));
  }

  /**
   * Check if a file mime type matches any allowed type (supports wildcards).
   */
  private isFileMimeTypeAccepted(
    supportedDocumentTypes: string,
    fileMimeType: string,
    delimiter = ', ',
  ): boolean {
    return supportedDocumentTypes.split(delimiter).some((accepted) => {
      const regexPattern = new RegExp(`^${accepted.replace('*', '.*')}$`, 'i');
      return regexPattern.test(fileMimeType);
    });
  }

  /**
   * Validate size against the bulk upload limit.
   */
  private checkFileExceedSizeLimit(file: File) {
    const sizeInMB = file.size / (1024 * 1024);
    return sizeInMB > this.MAX_FILE_SIZE_IN_MB;
  }

  /**
   * Translate a validation error into a user-facing message.
   */
  private getErrorMessage(error: {
    error: FileUploadError;
    file?: File;
    maxFiles?: number;
  }): string {
    const file = error.file;
    switch (error.error) {
      case FileUploadError.SizeLimitExceed:
        return this.translateService.instant(
          `${file?.name ?? 'File'} exceeds file limit of ${this.MAX_FILE_SIZE_IN_MB} MB`,
        );
      case FileUploadError.NotSupportedType:
        return this.translateService.instant(
          `Sorry, files of type ${this.getFileExtensionBasedOnLastDot(
            file?.name ?? '',
          )} are not supported.`,
        );
      case FileUploadError.TooLongFileName:
        return this.translateService.instant(
          `File name should not exceed ${MAX_FILE_NAME_CHARS} characters`,
        );
      case FileUploadError.TooManyFiles:
        return this.translateService.instant('You can only upload up to {{count}} files at once', {
          count: error.maxFiles ?? 0,
        });
      case FileUploadError.Other:
      default:
        return this.translateService.instant(
          'Failed to upload file. please try again or contact support if the issue persists',
        );
    }
  }

  /**
   * Resolve the file extension for error messaging (based on the last dot).
   */
  private getFileExtensionBasedOnLastDot(fileName: string) {
    if (!fileName) {
      return '';
    }
    return fileName.split('.').pop();
  }
}
